import React from 'react'

type PressurePoint = {
  timestamp: string
  scores?: Record<string, number>
  deltas?: Record<string, number>
  topDrivers?: Array<{ category?: string; label: string; scoreImpact?: number; evidenceRefs?: string[] }>
}

type PressureSeries = {
  region?: { mode?: string }
  categories?: string[]
  series?: PressurePoint[]
}

type PressureDetail = {
  category: string
  pressureScore: number
  delta: number
  timestamp: string
  drivers?: Array<{ label: string; scoreImpact?: number }>
}

type PressurePreset = {
  id: string
  label: string
  center: { lat: number; lon: number }
  radiusKm: number
}

type Props = {
  viewBounds: any
  pressureSeries: PressureSeries | null
  pressureLoading: boolean
  pressureError: string
  pressureDetail: PressureDetail | null
  activePressureCategory: string
  visiblePressureCategories: string[]
  pressureRegionMode: 'viewport' | 'radius'
  pressureRadiusKm: number
  pressurePresets: PressurePreset[]
  activePressurePresetId: string
  setActivePressureCategory: (value: string) => void
  setVisiblePressureCategories: React.Dispatch<React.SetStateAction<string[]>>
  setPressureRegionMode: (value: 'viewport' | 'radius') => void
  setPressureRadiusKm: (value: number) => void
  onSelectPressurePreset: (presetId: string) => void
  onSelectSpike: (timestamp: string) => void
}

export default function PressurePanel({
  viewBounds,
  pressureSeries,
  pressureLoading,
  pressureError,
  pressureDetail,
  activePressureCategory,
  visiblePressureCategories,
  pressureRegionMode,
  pressureRadiusKm,
  pressurePresets,
  activePressurePresetId,
  setActivePressureCategory,
  setVisiblePressureCategories,
  setPressureRegionMode,
  setPressureRadiusKm,
  onSelectPressurePreset,
  onSelectSpike,
}: Props) {
  return (
    <div style={{ padding: '18px 14px 12px', borderTop: '1px solid rgba(140,170,190,0.05)', borderBottom: '1px solid rgba(140,170,190,0.04)', background: 'rgba(3,9,16,0.14)', backdropFilter: 'blur(12px)', position: 'relative', zIndex: 6, boxShadow: '0 18px 42px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <div>
          <div className="eyebrow">Regional Multi-Domain Pressure Index</div>
          <div style={{ fontSize: 11, color: '#6f879b' }}>{viewBounds ? 'Viewport-linked pressure, last 24 hours' : 'Move the globe to calculate regional pressure'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setPressureRegionMode('viewport')}
            style={{ fontSize: 10, padding: '4px 8px', borderRadius: 999, border: '1px solid rgba(140,170,190,0.14)', background: pressureRegionMode === 'viewport' ? 'rgba(255,255,255,0.08)' : 'transparent', color: pressureRegionMode === 'viewport' ? '#fff' : '#89a3b8', cursor: 'pointer' }}
          >
            VIEWPORT
          </button>
          <button
            onClick={() => setPressureRegionMode('radius')}
            style={{ fontSize: 10, padding: '4px 8px', borderRadius: 999, border: '1px solid rgba(140,170,190,0.14)', background: pressureRegionMode === 'radius' ? 'rgba(255,255,255,0.08)' : 'transparent', color: pressureRegionMode === 'radius' ? '#fff' : '#89a3b8', cursor: 'pointer' }}
          >
            RADIUS
          </button>
          <div style={{ fontSize: 10, color: pressureError ? '#ff9b6b' : '#6f879b', fontFamily: 'monospace' }}>{pressureLoading ? 'loading…' : (pressureError || (pressureSeries?.region?.mode ? String(pressureSeries.region.mode).toUpperCase() : 'idle'))}</div>
        </div>
      </div>
      {pressureRegionMode === 'radius' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {pressurePresets.map((preset) => {
              const active = activePressurePresetId === preset.id
              return (
                <button
                  key={preset.id}
                  onClick={() => onSelectPressurePreset(preset.id)}
                  style={{
                    fontSize: 10,
                    padding: '4px 8px',
                    borderRadius: 999,
                    border: '1px solid rgba(140,170,190,0.14)',
                    background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                    color: active ? '#fff' : '#89a3b8',
                    cursor: 'pointer',
                  }}
                >
                  {preset.label}
                </button>
              )
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: '#89a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Radius</div>
            <input
              type="range"
              min={100}
              max={3000}
              step={50}
              value={pressureRadiusKm}
              onChange={(event) => setPressureRadiusKm(Number(event.target.value))}
              style={{ flex: 1 }}
            />
            <div style={{ minWidth: 64, textAlign: 'right', fontSize: 10, color: '#d7e7f3', fontFamily: 'monospace' }}>{pressureRadiusKm} km</div>
          </div>
        </>
      )}
      <div className="tactical-timeline-graph" style={{ marginTop: 0, height: 150, position: 'relative', zIndex: 7, background: 'rgba(6, 12, 18, 0.16)', borderRadius: 16, backdropFilter: 'blur(10px)' }}>
        <div className="tactical-grid-lines">
          {[20, 40, 60, 80].map(level => (
            <span key={level} className="tactical-grid-line" style={{ bottom: `${level}%` }} />
          ))}
        </div>
        <svg className="tactical-line-overlay" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {(() => {
            const palette: Record<string, string> = {
              conflict: '#ff7454',
              energy: '#ffbf54',
              unrest: '#d16bff',
              weather: '#54d5ff',
              disaster: '#6cf0a8',
              transport: '#8ea7ff',
              total: '#ffffff',
            }
            const categories = [...(pressureSeries?.categories || []), 'total']
            return categories.map((category) => {
              const values = (pressureSeries?.series || []).map((pt: any) => pt?.scores?.[category] ?? 0)
              if (values.length === 0) return null
              const points = values.map((value: number, i: number) => {
                const x = values.length <= 1 ? 0 : (i / (values.length - 1)) * 100
                const y = 100 - Math.max(2, Math.min(98, value))
                return `${x},${y}`
              }).join(' ')
              const isActive = category === activePressureCategory || category === 'total'
              return <polyline key={category} points={points} fill="none" stroke={palette[category] || '#9fb3c6'} opacity={isActive ? 1 : 0.28} strokeWidth={category === 'total' ? '1.1' : (category === activePressureCategory ? '0.9' : '0.55')} strokeLinejoin="round" strokeLinecap="round" />
            })
          })()}
        </svg>
        <div className="tactical-line-hit-area">
          {(pressureSeries?.series || []).map((point: any, i: number) => (
            <button
              key={point.timestamp || i}
              className="timeline-bar-wrap tactical-line-hit"
              title={`${point.timestamp} · total ${point?.scores?.total ?? 0}`}
              onClick={() => onSelectSpike(point.timestamp)}
            />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
        {(pressureSeries?.categories || []).map((category: string) => {
          const active = activePressureCategory === category
          const visible = visiblePressureCategories.includes(category)
          return (
            <button
              key={category}
              onClick={() => setActivePressureCategory(category)}
              onDoubleClick={() => setVisiblePressureCategories(prev => prev.includes(category) ? prev.filter(item => item !== category) : [...prev, category])}
              style={{
                fontSize: 10,
                color: active ? '#ffffff' : (visible ? '#9fb3c6' : '#4f667c'),
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: '1px solid rgba(140,170,190,0.14)',
                borderRadius: 999,
                padding: '4px 8px',
                cursor: 'pointer',
              }}
              title="Click to focus, double-click to toggle visibility"
            >
              {category}
            </button>
          )
        })}
        <span style={{ fontSize: 10, color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '4px 2px' }}>total</span>
      </div>
      {pressureDetail && (
        <div style={{ marginTop: 10, padding: '10px 12px', border: '1px solid rgba(84,213,255,0.08)', borderRadius: 8, background: 'rgba(6, 11, 18, 0.30)', backdropFilter: 'blur(12px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: '#d7e7f3', fontFamily: 'monospace' }}>{pressureDetail.category} spike · {pressureDetail.pressureScore}</div>
            <div style={{ fontSize: 10, color: '#6f879b', fontFamily: 'monospace' }}>{pressureDetail.timestamp}</div>
          </div>
          <div style={{ fontSize: 10, color: '#9fb3c6', marginTop: 6 }}>Delta {pressureDetail.delta} · {(pressureDetail.drivers || []).length} drivers · {((pressureDetail.relatedEvents || []).length || 0)} related events</div>
          <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
            {(pressureDetail.drivers || []).slice(0, 4).map((driver: any, idx: number) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, fontSize: 10 }}>
                <div>
                  <div style={{ color: '#c8d8e8' }}>{driver.label}</div>
                  {!!driver.sourceSummary?.length && <div style={{ color: '#6f879b', fontSize: 9, marginTop: 2 }}>{driver.sourceSummary[0]?.excerpt}</div>}
                </div>
                <div style={{ color: '#89a3b8', fontFamily: 'monospace' }}>{Math.round((driver.scoreImpact || 0) * 100) / 100}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
